// pages/ConsultancyConfigPage.jsx (Admin Dashboard)
import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  CircularProgress,
  Paper,
  Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';
import Iconify from '../components/Iconify';
import Page from '../components/Page';

const ConsultancyConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [configId, setConfigId] = useState(null);

  const [formData, setFormData] = useState({
    consultancyAreas: [],
    consultancyList: [],
    videoEmbedUrl: '',
    pageContent: {
      mainTitle: '',
      subtitle: '',
      listTitle: ''
    }
  });

  useEffect(() => {
    fetchConsultancyConfig();
  }, []);

  const fetchConsultancyConfig = async () => {
    try {
      const response = await axios.get('https://cpccbe.org/api/consultancy/getconsultancyconfig');
      console.log('Fetched config:', response.data);
      setConfig(response.data);
      setConfigId(response.data._id);
      setFormData({
        consultancyAreas: response.data.consultancyAreas || [],
        consultancyList: response.data.consultancyList || [],
        videoEmbedUrl: response.data.videoEmbedUrl || '',
        pageContent: {
          mainTitle: response.data.pageContent?.mainTitle || 'CONSULTANCY AREAS',
          subtitle: response.data.pageContent?.subtitle || 'The Coimbatore Productivity Council undertake the following Consultancy Services to the industries:',
          listTitle: response.data.pageContent?.listTitle || 'List of Consultancies Done by CPC'
        }
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching consultancy config:', error);
      setMessage('No consultancy configuration found. You can create a new one.');
      setMessageType('info');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prevData) => ({
        ...prevData,
        [parent]: {
          ...prevData[parent],
          [child]: value
        }
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value
      }));
    }
  };

  // Consultancy Areas Functions
  const addConsultancyArea = () => {
    const newId = formData.consultancyAreas.length > 0 
      ? Math.max(...formData.consultancyAreas.map(a => a.id)) + 1 
      : 1;
    
    setFormData(prev => ({
      ...prev,
      consultancyAreas: [...prev.consultancyAreas, { id: newId, name: '' }]
    }));
  };

  const updateConsultancyArea = (id, value) => {
    setFormData(prev => ({
      ...prev,
      consultancyAreas: prev.consultancyAreas.map(area => 
        area.id === id ? { ...area, name: value } : area
      )
    }));
  };

  const deleteConsultancyArea = (id) => {
    setFormData(prev => ({
      ...prev,
      consultancyAreas: prev.consultancyAreas.filter(area => area.id !== id)
    }));
  };

  // Consultancy List Functions
  const addConsultancyItem = () => {
    const newId = formData.consultancyList.length > 0 
      ? Math.max(...formData.consultancyList.map(item => item.id)) + 1 
      : 1;
    
    setFormData(prev => ({
      ...prev,
      consultancyList: [...prev.consultancyList, { id: newId, title: '', description: '' }]
    }));
  };

  const updateConsultancyItem = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      consultancyList: prev.consultancyList.map(item => 
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  const deleteConsultancyItem = (id) => {
    setFormData(prev => ({
      ...prev,
      consultancyList: prev.consultancyList.filter(item => item.id !== id)
    }));
  };

  // Convert description between string and array
  const toggleDescriptionType = (id) => {
    setFormData(prev => ({
      ...prev,
      consultancyList: prev.consultancyList.map(item => {
        if (item.id === id) {
          if (Array.isArray(item.description)) {
            // Convert array to string
            return { ...item, description: item.description.join('\n') };
          }
            // Convert string to array
            return { ...item, description: item.description.split('\n').filter(line => line.trim()) };
          
        }
        return item;
      })
    }));
  };

  const handleSave = async () => {
    try {
      // Clean up data before saving
      const cleanedData = {
        ...formData,
        consultancyAreas: formData.consultancyAreas.filter(area => area.name.trim()),
        consultancyList: formData.consultancyList.filter(item => item.title.trim()).map(item => ({
          ...item,
          description: Array.isArray(item.description) 
            ? item.description.filter(desc => desc.trim())
            : item.description
        }))
      };

      if (configId) {
        const response = await axios.patch(
          `https://cpccbe.org/api/consultancy/updateconsultancyconfig/${configId}`,
          cleanedData
        );
        setConfig(response.data);
        setMessage('Consultancy configuration updated successfully!');
        setMessageType('success');
      } else {
        const response = await axios.post(
          'https://cpccbe.org/api/consultancy/createconsultancyconfig',
          cleanedData
        );
        setConfig(response.data);
        setConfigId(response.data._id);
        setMessage('Consultancy configuration created successfully!');
        setMessageType('success');
      }
      setIsEditing(false);
      
      setTimeout(() => {
        setMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving consultancy config:', error);
      setMessage('Error saving consultancy configuration. Please try again.');
      setMessageType('error');
    }
  };

  const handleCancel = () => {
    if (config) {
      setFormData({
        consultancyAreas: config.consultancyAreas || [],
        consultancyList: config.consultancyList || [],
        videoEmbedUrl: config.videoEmbedUrl || '',
        pageContent: config.pageContent || {}
      });
    }
    setIsEditing(false);
    setMessage('');
  };

  if (loading) {
    return (
      <Page title="Consultancy Configuration">
        <Container>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
            <CircularProgress />
          </Box>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Consultancy Configuration">
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">
            Consultancy Configuration
          </Typography>
          {!isEditing ? (
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:edit-fill" />}
              onClick={() => setIsEditing(true)}
            >
              Edit Configuration
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Iconify icon="eva:close-fill" />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:save-fill" />}
                onClick={handleSave}
              >
                Save Changes
              </Button>
            </Box>
          )}
        </Box>

        {message && (
          <Alert severity={messageType} sx={{ mb: 3 }}>
            {message}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Page Content */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Iconify icon="eva:file-text-fill" />
                  Page Content
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Main Title"
                      name="pageContent.mainTitle"
                      value={formData.pageContent.mainTitle}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Subtitle"
                      name="pageContent.subtitle"
                      value={formData.pageContent.subtitle}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={2}
                      disabled={!isEditing}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="List Title"
                      name="pageContent.listTitle"
                      value={formData.pageContent.listTitle}
                      onChange={handleChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Video URL */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Iconify icon="eva:video-fill" />
                  Video Configuration
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <TextField
                  label="YouTube Video Embed URL"
                  name="videoEmbedUrl"
                  value={formData.videoEmbedUrl}
                  onChange={handleChange}
                  fullWidth
                  disabled={!isEditing}
                  helperText="Format: https://www.youtube.com/embed/VIDEO_ID"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Consultancy Areas */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Iconify icon="eva:list-fill" />
                    Consultancy Areas ({formData.consultancyAreas.length})
                  </Typography>
                  {isEditing && (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addConsultancyArea}
                      variant="outlined"
                      size="small"
                    >
                      Add Area
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  {formData.consultancyAreas.map((area, index) => (
                    <Grid item xs={12} key={area.id}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Chip label={index + 1} size="small" color="primary" />
                        <TextField
                          value={area.name}
                          onChange={(e) => updateConsultancyArea(area.id, e.target.value)}
                          fullWidth
                          size="small"
                          disabled={!isEditing}
                          placeholder="Consultancy area name"
                        />
                        {isEditing && (
                          <IconButton 
                            onClick={() => deleteConsultancyArea(area.id)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                  ))}
                  
                  {formData.consultancyAreas.length === 0 && (
                    <Grid item xs={12}>
                      <Alert severity="info">No consultancy areas added yet. Click "Add Area" to start.</Alert>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Consultancy List */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Iconify icon="eva:briefcase-fill" />
                    List of Consultancies Done ({formData.consultancyList.length})
                  </Typography>
                  {isEditing && (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addConsultancyItem}
                      variant="outlined"
                      size="small"
                    >
                      Add Consultancy
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={3}>
                  {formData.consultancyList.map((item, index) => (
                    <Grid item xs={12} key={item.id}>
                      <Paper sx={{ p: 2, bgcolor: '#f5f9ff' }}>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                          <Chip label={index + 1} size="small" color="primary" />
                          <Typography variant="subtitle2">Consultancy #{item.id}</Typography>
                          {isEditing && (
                            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => toggleDescriptionType(item.id)}
                              >
                                {Array.isArray(item.description) ? 'Convert to Text' : 'Convert to List'}
                              </Button>
                              <IconButton 
                                onClick={() => deleteConsultancyItem(item.id)}
                                color="error"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              label="Title"
                              value={item.title}
                              onChange={(e) => updateConsultancyItem(item.id, 'title', e.target.value)}
                              fullWidth
                              disabled={!isEditing}
                              size="small"
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <TextField
                              label={Array.isArray(item.description) ? "Description (One per line)" : "Description"}
                              value={Array.isArray(item.description) ? item.description.join('\n') : item.description}
                              onChange={(e) => {
                                const newValue = Array.isArray(item.description)
                                  ? e.target.value.split('\n')
                                  : e.target.value;
                                updateConsultancyItem(item.id, 'description', newValue);
                              }}
                              fullWidth
                              multiline
                              rows={Array.isArray(item.description) ? 4 : 2}
                              disabled={!isEditing}
                              size="small"
                              helperText={Array.isArray(item.description) ? "Enter each company/location on a new line" : "Single description text"}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                  
                  {formData.consultancyList.length === 0 && (
                    <Grid item xs={12}>
                      <Alert severity="info">No consultancy items added yet. Click "Add Consultancy" to start.</Alert>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Buttons at Bottom */}
        {isEditing && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Iconify icon="eva:close-fill" />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<Iconify icon="eva:save-fill" />}
              onClick={handleSave}
            >
              Save Changes
            </Button>
          </Box>
        )}
      </Container>
    </Page>
  );
};

export default ConsultancyConfigPage;